import * as React from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";

import { Box, Button, Dialog, Typography } from "@mui/material";

import useSnackbarAlert from "../../../utils/snackbarHandlers";
import { parseBarcode, endNumberFromTicket } from "../../../utils/barcode";
import { COLORS } from "./constants";

interface PackReturnModalProps {
  open: boolean;
  onClose: () => void;
  socket: any;
  apiBaseURL: string;
  shopId: any;
  onReturned: () => void;
}

// Pack Return popup (Figma node 2346:146). Returns a confirmed/active pack by
// scan or manual game + pack entry. Mirrors the ActivateGameModal shell (orange
// header, orange divider, navy footer) but without the slot field. Stays open
// after each return so several packs can be returned back to back.
const PackReturnModal: React.FC<PackReturnModalProps> = ({ open, onClose, socket, apiBaseURL, shopId, onReturned }) => {
  const { t } = useTranslation("translation");
  const { showErrorMessage, showSuccessMessage } = useSnackbarAlert();

  const [scanCode, setScanCode] = React.useState<string>("");
  const [gameNumber, setGameNumber] = React.useState<string>("");
  const [packNumber, setPackNumber] = React.useState<string>("");
  // where an active pack is being closed - the number on the ticket now on top of it
  const [endNumber, setEndNumber] = React.useState<string>("");
  const [returning, setReturning] = React.useState<boolean>(false);
  const scanRef = React.useRef<HTMLInputElement | null>(null);

  // Guards against returning the same scanned ticket twice (a scanner fires a
  // burst of key events plus an Enter terminator).
  const processedScanRef = React.useRef<string>("");

  const clearFields = () => {
    setScanCode("");
    setGameNumber("");
    setPackNumber("");
    setEndNumber("");
    processedScanRef.current = "";
  };

  // put the cursor in the scan field whenever the popup opens, so a scanner can
  // fire straight into it without the user clicking first
  React.useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => scanRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [open]);

  React.useEffect(() => {
    if (!socket) return;
    const onSuccess = () => {
      setReturning(false);
      showSuccessMessage(t("packReturnSuccessMsg", "Pack returned successfully"));
      clearFields();
      onReturned();
    };
    const onError = (response: any) => {
      setReturning(false);
      showErrorMessage(response?.message ?? t("somethingWentWrong", "Something went wrong"));
    };
    socket.on("packReturnInventoryV2Response", onSuccess);
    socket.on("packReturnInventoryV2Error", onError);
    return () => {
      socket.off("packReturnInventoryV2Response", onSuccess);
      socket.off("packReturnInventoryV2Error", onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  // Return a pack. Takes explicit game/pack so a scan can submit freshly parsed
  // values without waiting for input state to update.
  const submitPackReturn = (game: string, pack: string, end: string) => {
    if (!socket) {
      return showErrorMessage(t("socketNotReady", "Connection not ready, please retry"));
    }
    if (!/^\d{4}$/.test(game)) {
      return showErrorMessage(t("gameNumberFourDigitsError", "The game number must be 4 digits."));
    }
    if (!/^\d{7}$/.test(pack)) {
      return showErrorMessage(t("packNumberSevenDigitsError", "The pack number should be 7 digits."));
    }
    setReturning(true);
    // the ending number only applies to an active (placed) pack - the server decides
    // whether it is required and validates it against the pack's start and size
    socket.emit("packReturnInventoryV2", { shopId, gameNumber: game, packNumber: pack, endNumber: end });
  };

  const handlePackReturn = () => submitPackReturn(gameNumber, packNumber, endNumber);

  // Scanning the ticket on top of the pack gives the game, the pack and the number
  // that pack is being closed at (the ticket number in brackets on the barcode).
  // Unlike Confirm, a scan here only fills the fields: a return closes the pack's
  // sales and frees its screen slot, so it stays an explicit button press the user
  // can check first.
  const fillFromScan = (code: string) => {
    if (code.length < 11) return;
    const scanned = parseBarcode(code);
    setGameNumber(scanned.gameNumber);
    setPackNumber(scanned.packNumber);
    // The barcode carries a ticket number, not the ending number printed beside it;
    // converting between them needs the pack size, so look the game up. If that
    // fails the box simply stays empty for manual entry.
    if (scanned.ticketNumber) {
      axios
        .post(`${apiBaseURL}/api/inventory-v2/gameDetails`, { gameNumber: scanned.gameNumber })
        .then((res: any) => {
          const end = endNumberFromTicket(scanned.ticketNumber, res?.data?.packSize);
          if (end) setEndNumber(end);
        })
        .catch(() => { /* leave the ending number for the user to type */ });
    }
  };

  const onScanChange = (value: string) => {
    setScanCode(value);
    fillFromScan(value);
  };

  const onScanKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    fillFromScan(scanCode.trim());
  };

  const onEntryKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (!returning) handlePackReturn();
  };

  const handleClose = () => {
    clearFields();
    onClose();
  };

  const labelSx = { fontSize: "18px", color: "#434343", mb: "6px" } as const;
  const inputSx = {
    width: "100%",
    height: "40px",
    borderRadius: "10px",
    border: `1px solid ${COLORS.border}`,
    backgroundColor: "#fff",
    px: "12px",
    outline: "none",
    fontSize: "17px",
  } as const;
  const footerBtnSx = {
    backgroundColor: COLORS.navy,
    color: "#fff",
    textTransform: "none",
    borderRadius: "2px",
    px: 3,
    fontSize: "16px",
    fontWeight: 600,
    letterSpacing: "1px",
    "&:hover": { backgroundColor: COLORS.navy, opacity: 0.9 },
  } as const;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Escape closes; a stray backdrop click must not discard what was typed
        if (reason === "backdropClick") return;
        handleClose();
      }}
      PaperProps={{
        sx: {
          width: "505px",
          maxWidth: "95vw",
          borderRadius: "10px",
          border: `2px solid ${COLORS.orange}`,
          backgroundColor: COLORS.panelBg,
          overflow: "hidden",
        },
      }}
    >
      {/* Orange header */}
      <Box sx={{ backgroundColor: COLORS.orange, height: "43px", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2 }}>
        <Typography sx={{ color: "#fff", fontSize: "22px", fontWeight: 600, letterSpacing: "1px" }}>
          {t("packReturnTitle", "Pack Return")}
        </Typography>
        <Box component='span' onClick={handleClose} sx={{ color: "#fff", fontSize: "26px", cursor: "pointer", lineHeight: 1 }} aria-label={t("close", "Close")}>
          ×
        </Box>
      </Box>

      <Box sx={{ p: 2.5 }}>
        <Box mb={1.5}>
          <Typography sx={labelSx}>{t("scanCodeLabel", "Scan Code")}</Typography>
          <Box component='input' ref={scanRef} autoFocus value={scanCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onScanChange(e.target.value)} onKeyDown={onScanKeyDown} sx={inputSx} />
        </Box>
        {/* Orange divider separating scan entry from manual entry (Figma node 2346:146) */}
        <Box sx={{ height: "2px", backgroundColor: COLORS.orange, mx: -1.5, mb: 2.5 }} />
        <Box mb={2}>
          <Typography sx={labelSx}>{t("gameNumberLabel", "Game Number")}</Typography>
          <Box component='input' value={gameNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGameNumber(e.target.value)} onKeyDown={onEntryKeyDown} sx={inputSx} />
        </Box>
        <Box mb={2}>
          <Typography sx={labelSx}>{t("packNumberLabel", "Pack Number")}</Typography>
          <Box component='input' value={packNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPackNumber(e.target.value)} onKeyDown={onEntryKeyDown} sx={inputSx} />
        </Box>
        <Box mb={1}>
          <Typography sx={labelSx}>{t("endNumberLabel", "Ending Number")}</Typography>
          <Box
            component='input'
            inputMode='numeric'
            value={endNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndNumber(e.target.value.replace(/[^0-9]/g, ""))}
            onKeyDown={onEntryKeyDown}
            sx={inputSx}
          />
          <Typography sx={{ fontSize: "14px", color: "#6b6b6b", mt: "4px" }}>
            {t("endNumberHint", "Required for an active pack - the number the pack is being returned at.")}
          </Typography>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ borderTop: `1px solid ${COLORS.orange}`, display: "flex", justifyContent: "flex-end", gap: "12px", p: 2 }}>
        <Button onClick={handleClose} disableElevation sx={footerBtnSx}>
          {t("close", "Close")}
        </Button>
        <Button onClick={handlePackReturn} disabled={returning} disableElevation sx={footerBtnSx}>
          {t("packReturnButton", "Pack Return")}
        </Button>
      </Box>
    </Dialog>
  );
};

export default PackReturnModal;
